/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 */

import {DateTime} from 'luxon';

// ISO date-time with explicit offset; date-only values (e.g. `2025-07`) must not match.
const ISO_DATE_TIME_WITH_OFFSET =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}(:?\d{2})?)$/i;

// JSON-schema `format` values that the ui-tree mapper may copy over; not Luxon tokens.
const SCHEMA_DATE_FORMATS = new Set([
  'date',
  'date-time',
  'time',
  'duration',
  'uri',
  'uri-reference',
  'iri',
  'iri-reference',
  'email',
  'idn-email',
  'hostname',
  'idn-hostname',
  'ipv4',
  'ipv6',
  'uuid',
  'regex',
  'json-pointer',
]);

/**
 * Formats an ISO date-time that carries an explicit offset with a Luxon `format`,
 * converted to `timeZone` or, when absent, to the browser's local zone, and localised
 * with `locale` (the UI language) when given.
 *
 * Returns `value` unchanged when it is not a date-time with offset (e.g. `2025-07`),
 * is not a valid date, or when `format` is missing or a JSON-schema format.
 */
export function formatDateTimeValue<T>(
  value: T,
  format?: string,
  timeZone?: string,
  locale?: string,
): T | string {
  if (
    typeof value !== 'string' ||
    !format ||
    SCHEMA_DATE_FORMATS.has(format) ||
    !ISO_DATE_TIME_WITH_OFFSET.test(value)
  ) {
    return value;
  }
  const zoned = DateTime.fromISO(value, {setZone: true});
  const zonedValue = timeZone ? zoned.setZone(timeZone) : zoned.toLocal();
  const parsed = locale ? zonedValue.setLocale(locale) : zonedValue;
  return parsed.isValid ? parsed.toFormat(format) : value;
}
